// Client-side exporters over a filtered set of logs. CSV is one file per
// activity type (schemas differ); JSON keeps the typed structure including
// schemas; Markdown is grouped by type then date. Currency CSV cells are
// dollars rendered from integer minor units (H-7). The bundle zip is
// store-only (no compression) so this module has no zip-library dependency.

import type { ActivityField, ActivityLog, ActivityType, FieldValue } from "@lifelog/shared";

export function formatCurrencyDollars(minorUnits: number): string {
  return (minorUnits / 100).toFixed(2);
}

export function formatFieldValue(field: ActivityField | undefined, value: FieldValue): string {
  if (field?.kind === "currency" && typeof value === "number") {
    return formatCurrencyDollars(value);
  }
  if (field?.kind === "boolean") return value ? "true" : "false";
  if (field?.kind === "duration" && typeof value === "number") {
    return field.unit ? `${value} ${field.unit}` : String(value);
  }
  return String(value);
}

/** RFC 4180: wrap if the value contains comma, quote, or newline; double quotes. */
export function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function fieldHeader(field: ActivityField): string {
  if (field.kind === "currency") {
    return field.unit ? `${field.label} (${field.unit})` : field.label;
  }
  if ((field.kind === "number" || field.kind === "duration") && field.unit) {
    return `${field.label} (${field.unit})`;
  }
  return field.label;
}

export type NameLookup = (userId: string | undefined) => string;

function csvCell(field: ActivityField | undefined, value: FieldValue | undefined): string {
  if (value === undefined) return "";
  return csvEscape(formatFieldValue(field, value));
}

export function logsToCsv(
  type: ActivityType,
  logs: ActivityLog[],
  nameOf: NameLookup,
): string {
  const fields = [...type.fields].sort((a, b) => a.order - b.order);
  const headers = [
    "occurredAt",
    "loggedBy",
    "performedBy",
    "note",
    ...fields.map(fieldHeader),
  ];
  const rows = logs.map((log) => {
    const cells = [
      csvEscape(log.occurredAt),
      csvEscape(nameOf(log.loggedByUserId)),
      csvEscape(nameOf(log.performedByUserId)),
      csvEscape(log.note ?? ""),
      ...fields.map((f) => csvCell(f, log.values[f.fieldId])),
    ];
    return cells.join(",");
  });
  return [headers.map(csvEscape).join(","), ...rows].join("\r\n") + (rows.length ? "\r\n" : "");
}

export interface ExportJson {
  exportedAt: string;
  types: ActivityType[];
  logs: ActivityLog[];
}

export function logsToJson(types: ActivityType[], logs: ActivityLog[], exportedAt: string): string {
  const body: ExportJson = { exportedAt, types, logs };
  return JSON.stringify(body, null, 2);
}

function formatDayHeading(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function logsToMarkdown(
  types: ActivityType[],
  logs: ActivityLog[],
  nameOf: NameLookup,
): string {
  const typeById = new Map(types.map((t) => [t.activityTypeId, t]));
  const grouped = new Map<string, ActivityLog[]>();
  for (const log of logs) {
    const arr = grouped.get(log.activityTypeId) ?? [];
    arr.push(log);
    grouped.set(log.activityTypeId, arr);
  }

  const lines: string[] = ["# Life Logger export", "", `_Exported ${new Date().toLocaleString()}_`, ""];

  const typeOrder = [...grouped.keys()].sort((a, b) => {
    const na = typeById.get(a)?.name ?? a;
    const nb = typeById.get(b)?.name ?? b;
    return na.localeCompare(nb);
  });

  for (const typeId of typeOrder) {
    const type = typeById.get(typeId);
    const items = grouped.get(typeId) ?? [];
    lines.push(`## ${type?.name ?? typeId}`);
    lines.push("");

    const byDate = new Map<string, ActivityLog[]>();
    for (const log of items) {
      const day = log.occurredAt.slice(0, 10);
      const arr = byDate.get(day) ?? [];
      arr.push(log);
      byDate.set(day, arr);
    }
    for (const [, dayLogs] of [...byDate.entries()].sort(([a], [b]) => b.localeCompare(a))) {
      for (const log of dayLogs) {
        lines.push(`### ${formatDayHeading(log.occurredAt)}`);
        const fields = [...(type?.fields ?? [])].sort((a, b) => a.order - b.order);
        for (const field of fields) {
          const value = log.values[field.fieldId];
          if (value === undefined) continue;
          lines.push(`- **${field.label}:** ${formatFieldValue(field, value)}`);
        }
        if (log.note) lines.push(`- **Note:** ${log.note}`);
        const who = nameOf(log.performedByUserId);
        if (who) lines.push(`- **Performed by:** ${who}`);
        lines.push("");
      }
    }
  }
  return lines.join("\n");
}

export function exportReadme(exportedAt: string): string {
  return [
    "# Life Logger export bundle",
    "",
    `Exported: ${exportedAt}`,
    "",
    "This zip is self-describing. It contains:",
    "",
    "- `data.json` — full typed logs plus the activity-type schemas they were written against.",
    "  Currency values in JSON are integer minor units (cents). This is the format meant for",
    "  feeding a larger model or re-importing later.",
    "- `log.md` — the same logs grouped by activity type, then by date, as readable Markdown.",
    "- `csv/` — one CSV per activity type (schemas differ, so they cannot share a header row).",
    "  Columns: `occurredAt`, `loggedBy`, `performedBy`, `note`, then one column per field.",
    "  Currency columns render as dollars (e.g. stored `1299` → `12.99`).",
    "",
    "CSV follows RFC 4180: commas, double quotes, and newlines inside a value are quoted,",
    "and double quotes inside a quoted value are doubled.",
    "",
  ].join("\n");
}

function slug(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "activity";
}

export interface ExportFile {
  name: string;
  content: string;
}

export function buildExportFiles(
  types: ActivityType[],
  logs: ActivityLog[],
  nameOf: NameLookup,
  exportedAt = new Date().toISOString(),
): ExportFile[] {
  const typeById = new Map(types.map((t) => [t.activityTypeId, t]));
  const usedTypeIds = [...new Set(logs.map((l) => l.activityTypeId))];
  const files: ExportFile[] = [
    { name: "README.md", content: exportReadme(exportedAt) },
    { name: "data.json", content: logsToJson(types, logs, exportedAt) },
    { name: "log.md", content: logsToMarkdown(types, logs, nameOf) },
  ];
  for (const typeId of usedTypeIds) {
    const type = typeById.get(typeId);
    if (!type) continue;
    const typeLogs = logs.filter((l) => l.activityTypeId === typeId);
    files.push({
      name: `csv/${slug(type.shortName)}-${type.activityTypeId.slice(0, 8)}.csv`,
      content: logsToCsv(type, typeLogs, nameOf),
    });
  }
  return files;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c;
  }
  return table;
})();

function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function u16(n: number): Uint8Array {
  const b = new Uint8Array(2);
  b[0] = n & 0xff;
  b[1] = (n >>> 8) & 0xff;
  return b;
}

function u32(n: number): Uint8Array {
  const b = new Uint8Array(4);
  b[0] = n & 0xff;
  b[1] = (n >>> 8) & 0xff;
  b[2] = (n >>> 16) & 0xff;
  b[3] = (n >>> 24) & 0xff;
  return b;
}

function concat(parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

const encoder = new TextEncoder();

/** Store-only ZIP (method 0). Enough for a handful of text files. */
export function zipStore(files: ExportFile[]): Uint8Array {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const crc = crc32(data);
    const local = concat([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      data,
    ]);
    const central = concat([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }

  const centralDir = concat(centrals);
  const eocd = concat([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.length),
    u32(offset),
    u16(0),
  ]);
  return concat([...locals, centralDir, eocd]);
}

export function downloadBytes(filename: string, bytes: Uint8Array, mime: string): void {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadExportBundle(
  types: ActivityType[],
  logs: ActivityLog[],
  nameOf: NameLookup,
): void {
  const exportedAt = new Date().toISOString();
  const files = buildExportFiles(types, logs, nameOf, exportedAt);
  const zip = zipStore(files);
  const day = exportedAt.slice(0, 10);
  downloadBytes(`life-logger-export-${day}.zip`, zip, "application/zip");
}
